import { Injectable, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SessionStatus } from '@shared/session-status.model';
import { distinctUntilChanged, finalize, map, Observable, Subject } from 'rxjs';

import { CallId } from '../models/call.model';
import { CallQualitySummary, CallQualityTier } from '../models/phone.models';
import { RtcStat } from '../models/rtc-stat.model';
import { SipjsService } from './sipjs.service';

interface QualityTick {
  timestampMs: number;
  jitterMs: number;
  /** Undefined when neither remote-inbound-rtp nor the candidate pair reported a round trip -- not a real 0ms measurement. */
  latencyMs: number | undefined;
  /** Undefined before there's a prior packet count for this SSRC to diff against -- not a real 0%/measurement. */
  packetLossPct: number | undefined;
}

/**
 * Live per-metric snapshot for the call quality detail panel. The headline `valueMs`/`valuePct`
 * fields are the same smoothed, rolling-window-averaged values that drive the live badge tier (a
 * single source of truth between badge color and panel numbers); the rest of each metric is raw,
 * latest-tick diagnostic detail for the panel's "Advanced Stats" section.
 */
export interface CallQualityMetricsSnapshot {
  overallTier: CallQualityTier;
  jitter: {
    valueMs: number;
    tier: CallQualityTier;
    jitterBufferMs: number;
  };
  latency: {
    valueMs: number;
    tier: CallQualityTier;
    rttMs: number;
    relayed: boolean;
  };
  packetLoss: {
    valuePct: number;
    tier: CallQualityTier;
    packetsSent: number;
    packetsReceived: number;
    packetsLost: number;
  };
}

interface CallMonitor {
  intervalId?: ReturnType<typeof setInterval>;
  /**
   * A plain `Subject`, not a `BehaviorSubject` -- deliberately has no seeded initial value. Emitting
   * a fake "Excellent" default before the first real tick would show a confident answer (green
   * badge) based on zero measurements, indistinguishable from an actually-measured excellent call.
   * Subscribers simply get nothing until the first real sample lands.
   */
  metricsSubject: Subject<CallQualityMetricsSnapshot>;
  /**
   * Count of live `observeQuality`/`observeMetrics` subscribers, so a monitor that was only ever
   * created lazily (by a subscriber, before `startMonitoring` ran) can be cleaned up once that
   * subscriber goes away instead of sitting in `monitors` forever -- see `releaseSubscriber`.
   */
  subscriberCount: number;
  /** Raw ticks within the live smoothing window, used to average metrics for the displayed tier. */
  rollingTicks: QualityTick[];
  /**
   * One unsmoothed, classified entry per tick for the whole call, used for the end-of-call rollup.
   * `tier` is the combined (worst-of-three) tier; the per-metric tiers are kept alongside so each
   * metric can also get its own independent whole-call rollup.
   */
  history: Array<{
    timestampMs: number;
    tier: CallQualityTier;
    jitterTier: CallQualityTier;
    latencyTier: CallQualityTier;
    packetLossTier: CallQualityTier;
  }>;
  /** Running sums of each raw (unsmoothed) per-tick metric, for the whole-call average reported at call end. */
  jitterSumMs: number;
  latencySumMs: number;
  /**
   * Number of ticks that actually contributed to `latencySumMs` -- excludes ticks with no RTT
   * signal at all (neither remote-inbound-rtp nor the candidate pair reported one), so a stretch of
   * missing signal doesn't get averaged in as if it were a real 0ms reading and drag the whole-call
   * average down.
   */
  latencySampleCount: number;
  /**
   * Running sums of lost/total packets across every tick, for a volume-weighted whole-call packet
   * loss average (total lost / total received+lost) -- NOT a flat average of each tick's percentage.
   * A flat average would give a quiet tick with 1 lost packet out of 2 (50%) the same weight as a
   * busy tick with 1 lost out of 200 (0.5%), letting a couple of stray packets during silence skew
   * the whole-call number even though they're negligible in volume.
   */
  packetsLostSum: number;
  packetsTotalSum: number;
  /**
   * Packet-loss delta baseline, keyed by SSRC rather than a flat scalar -- each inbound-rtp SSRC's
   * own `packetsReceived`/`packetsLost` counters are independently monotonic (and freeze while that
   * SSRC isn't the active stream), so diffing against the *previous tick's* counts regardless of
   * which SSRC they came from produces nonsense the moment the stream switches (e.g. a MOH
   * substitution): the new SSRC's small counter minus the old SSRC's large one goes negative.
   * Keying by SSRC means a stream that switches away and later back gets a correct delta both times.
   */
  packetBaselineBySsrc: Map<number, { packetsReceived: number; packetsLost: number }>;
  /** True while a `sampleTick` is in flight, so a slow `getStats()` can't let two ticks overlap and corrupt the packet-loss delta state. */
  sampling?: boolean;
  /**
   * True while the call is on hold. Sampling is paused entirely while held -- the peer connection's
   * `getStats()` would just be reporting the MOH/silence path, not real call media, so there's
   * nothing meaningful to sample.
   */
  held: boolean;
  /**
   * Wall-clock (epoch ms, same timeline as `history[].timestampMs`) intervals the call has spent on
   * hold. The most recent entry's `endMs` is left unset while a hold is still in progress. Passed to
   * `rollupTiers` so held time is excluded from the quality rollup entirely, rather than having the
   * pre-hold tier carried forward across the gap (see `rollupTiers`'s last-observation-carried-forward
   * duration logic).
   */
  heldIntervals: Array<{ startMs: number; endMs?: number }>;
  /**
   * The inbound-rtp SSRC seen on the call's first tick -- i.e. the real caller's media stream.
   * Undefined until that first tick lands. Some hold implementations substitute an entirely
   * different RTP source (a new SSRC) for MOH server-side, with no SDP/direction change at all --
   * see `sampleTick`'s comparison against this baseline for why that's checked on every tick rather
   * than treated as an event.
   */
  primarySsrc?: number;
}

/**
 * Tracks live call quality (jitter/latency/packet-loss) per call from WebRTC `getStats()`, so the
 * UI can show a real-time signal indicator and the RTC recap log can capture a whole-call quality
 * rollup rather than just the final tick's values.
 */
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CallQualityService implements OnDestroy {
  /** How often the peer connection's stats are sampled. Polls more often than the UI needs so the rolling average has enough data points. */
  private static readonly pollIntervalMs = 1000;
  /** Window of recent ticks averaged together for the live, displayed tier. */
  private static readonly rollingWindowMs = 8000;

  /** Exposed so consumers like `CallQualityPanelComponent` can keep their gauge zones in sync with what `classify()` actually does. */
  public static readonly jitterThresholdsMs = { poor: 30, bad: 50 };
  public static readonly latencyThresholdsMs = { poor: 150, bad: 300 };
  public static readonly packetLossThresholdsPct = { poor: 1, bad: 3 };

  /**
   * Gauge scale tops (visual axis only, no classify() equivalent) plus the same poor/bad
   * thresholds above, bundled per metric for consumers that render a gauge -- the live panel and
   * the historical call-history summary. Deriving poor/bad from the statics above (rather than a
   * second set of hardcoded numbers) means both gauges can't drift from what classify() does.
   */
  public static readonly gaugeConfig: Record<
    'jitter' | 'latency' | 'packetLoss',
    { max: number; poorThreshold: number; badThreshold: number }
  > = {
    jitter: {
      max: 100,
      poorThreshold: CallQualityService.jitterThresholdsMs.poor,
      badThreshold: CallQualityService.jitterThresholdsMs.bad,
    },
    latency: {
      max: 400,
      poorThreshold: CallQualityService.latencyThresholdsMs.poor,
      badThreshold: CallQualityService.latencyThresholdsMs.bad,
    },
    packetLoss: {
      max: 10,
      poorThreshold: CallQualityService.packetLossThresholdsPct.poor,
      badThreshold: CallQualityService.packetLossThresholdsPct.bad,
    },
  };

  /** Size of the sliding window used to find the single worst stretch of the call. */
  private static readonly worstWindowMs = 30000;
  /** worstPoints at or above this (but at or below `badPointsLimit`) marks the whole call Poor. */
  private static readonly poorPointsLimit = 15;
  /** worstPoints above this marks the whole call Bad. */
  private static readonly badPointsLimit = 45;
  /**
   * Points that one second at each tier contributes to the worst-window total. `NoData` is never
   * actually multiplied against a nonzero duration -- `rollupTiers` zeroes a `NoData` sample's own
   * duration outright, excluding it from the score entirely rather than scoring it as 0/neutral --
   * but the entry still has to exist to satisfy `Record<CallQualityTier, number>`.
   */
  private static readonly tierPoints: Record<CallQualityTier, number> = {
    [CallQualityTier.Excellent]: 0,
    [CallQualityTier.Poor]: 1,
    [CallQualityTier.Bad]: 3,
    [CallQualityTier.NoData]: 0,
  };

  /** `NoData` sits below `Excellent` so `combineWorst` never lets an unmeasured metric mask a real Poor/Bad reading from another metric. */
  private static readonly tierSeverity: Record<CallQualityTier, number> = {
    [CallQualityTier.NoData]: -1,
    [CallQualityTier.Excellent]: 0,
    [CallQualityTier.Poor]: 1,
    [CallQualityTier.Bad]: 2,
  };

  private readonly monitors = new Map<CallId, CallMonitor>();

  /**
   * Emits the whole-call summary as soon as it's known, i.e. when `stopMonitoring` runs at call
   * teardown. `callId` here is SipjsService's normalized id (the plain SIP `Call-ID`, tag
   * stripped), not the raw `CallId`/`session.id` used internally by `monitors` -- consumers like
   * `CallQualitySummaryService` correlate this against CallHistory's `origCallid`/`termCallid`,
   * which are plain strings in that same normalized form, not the tag-decorated session id.
   */
  private readonly summaryReadySubject = new Subject<{ callId: string; summary: CallQualitySummary }>();
  public readonly summaryReady$ = this.summaryReadySubject.asObservable();

  constructor(private sipJsService: SipjsService) {
    this.sipJsService.callMedia$.pipe(untilDestroyed(this)).subscribe(({ callId, peerConnection, phase }) => {
      if (phase === 'opened' && peerConnection) {
        this.startMonitoring(callId, peerConnection);
      } else if (phase === 'closing') {
        this.logCallSummary(callId);
        this.stopMonitoring(callId);
      }
    });

    // Hold/unhold re-INVITEs reuse the same peer connection (no `callMedia$` event), so hold state
    // is tracked separately here rather than folded into the 'opened'/'closing' lifecycle above.
    // This only ever reflects a LOCALLY-initiated hold (see `SipjsService.onCallHold`) -- remote
    // hold is handled separately, per-tick, via the SSRC-substitution check in `sampleTick`.
    this.sipJsService.sessionStatus$.pipe(untilDestroyed(this)).subscribe(({ call, status }) => {
      const monitor = this.monitors.get(call.id);
      if (!monitor) {
        return;
      }

      if (status === SessionStatus.Hold && !monitor.held) {
        this.beginHold(monitor);
      } else if (status === SessionStatus.Answered && monitor.held) {
        this.endHold(monitor);
      }
    });
  }

  private beginHold(monitor: CallMonitor): void {
    monitor.held = true;
    monitor.heldIntervals.push({ startMs: Date.now() });

    // sampleTick stops emitting entirely while held, which would otherwise leave the live badge
    // frozen on whatever tier it happened to show the instant before hold -- including a stale
    // Bad/Poor reading that has nothing to do with the call anymore. Push an explicit "no data"
    // snapshot now so the badge reflects reality: there's nothing to measure while held.
    monitor.metricsSubject.next({
      overallTier: CallQualityTier.NoData,
      jitter: { valueMs: 0, tier: CallQualityTier.NoData, jitterBufferMs: 0 },
      latency: { valueMs: 0, tier: CallQualityTier.NoData, rttMs: 0, relayed: false },
      packetLoss: { valuePct: 0, tier: CallQualityTier.NoData, packetsSent: 0, packetsReceived: 0, packetsLost: 0 },
    });
  }

  private endHold(monitor: CallMonitor): void {
    monitor.held = false;
    const currentHold = monitor.heldIntervals.at(-1);
    if (currentHold) {
      currentHold.endMs = Date.now();
    }

    // Neither the packet-loss delta baseline nor the live smoothing window advanced while held
    // (sampleTick never ran), so both are stale as of *before* hold started. Without resetting
    // them, the first real tick after unhold would diff its packet counts against that stale
    // baseline -- spanning the entire hold as if it were one tick -- and read as a bogus loss
    // spike, and the smoothing window would blend real post-unhold data with stale pre-hold
    // ticks. Clearing both makes the resumed call start measuring fresh, the same way a
    // brand-new call has no baseline yet.
    monitor.packetBaselineBySsrc.clear();
    monitor.rollingTicks = [];
  }

  /** Begins sampling the peer connection every `pollIntervalMs` for the given call. No-op if already monitoring this call. */
  public startMonitoring(callId: CallId, peerConnection: RTCPeerConnection): void {
    const monitor = this.getOrCreateMonitor(callId);
    if (monitor.intervalId !== undefined) {
      return;
    }

    monitor.intervalId = setInterval(() => {
      void this.sampleTick(callId, peerConnection);
    }, CallQualityService.pollIntervalMs);
  }

  /** Emits the live, smoothed quality tier for the given call. Can be subscribed to before monitoring starts. */
  public observeQuality(callId: CallId): Observable<CallQualityTier> {
    const monitor = this.getOrCreateMonitor(callId);
    monitor.subscriberCount++;
    return monitor.metricsSubject.pipe(
      map((snapshot) => snapshot.overallTier),
      distinctUntilChanged(),
      finalize(() => this.releaseSubscriber(callId))
    );
  }

  /** Emits the live, smoothed per-metric snapshot (values + advanced diagnostics) for the call quality detail panel. */
  public observeMetrics(callId: CallId): Observable<CallQualityMetricsSnapshot> {
    const monitor = this.getOrCreateMonitor(callId);
    monitor.subscriberCount++;
    return monitor.metricsSubject.asObservable().pipe(finalize(() => this.releaseSubscriber(callId)));
  }

  /**
   * Drops a subscriber registered by `observeQuality`/`observeMetrics`. If that was the last one and
   * `startMonitoring` never ran for this call (e.g. an outgoing call is subscribed to at
   * `SessionStatus.Created`, then cancelled/rejected before SIP.js ever reaches Established), the
   * monitor was never going anywhere else -- clean it up now instead of leaving an orphaned entry in
   * `monitors` for the life of the app. A monitor that IS being sampled is left alone here: it's
   * cleaned up by `stopMonitoring` on the call's own teardown, independent of UI subscribers coming
   * and going.
   */
  private releaseSubscriber(callId: CallId): void {
    const monitor = this.monitors.get(callId);
    if (!monitor) {
      return;
    }

    monitor.subscriberCount--;
    if (monitor.subscriberCount <= 0 && monitor.intervalId === undefined) {
      monitor.metricsSubject.complete();
      this.monitors.delete(callId);
    }
  }

  /** Reduces the whole call's history into one overall tier. Must be called before `stopMonitoring`, which clears the history. */
  public getCallSummary(callId: CallId): CallQualitySummary | undefined {
    const monitor = this.monitors.get(callId);
    if (!monitor || monitor.history.length === 0) {
      return undefined;
    }

    return this.buildSummary(monitor);
  }

  /**
   * Logs the whole-call quality rollup and emits `summaryReady$` for consumers like
   * `CallQualitySummaryService`. Must be called before `stopMonitoring`, which clears the history.
   */
  private logCallSummary(callId: CallId): void {
    const summary = this.getCallSummary(callId);
    if (!summary) {
      return;
    }

    console.log(
      `[RTC-RECAP] callId=${callId} overallQuality=${summary.tier} worstPoints=${summary.worstPoints}` +
        ` jitterTier=${summary.jitterTier} jitterAvgMs=${summary.jitterAvgMs.toFixed(1)}` +
        ` latencyTier=${summary.latencyTier} latencyAvgMs=${summary.latencyAvgMs.toFixed(1)}` +
        ` packetLossTier=${summary.packetLossTier}` +
        ` packetLossAvgPct=${summary.packetLossAvgPct.toFixed(2)}`
    );

    this.summaryReadySubject.next({ callId: this.sipJsService.getNormalizedCallId(callId), summary });
  }

  /** Stops sampling and discards all state for the given call. */
  public stopMonitoring(callId: CallId): void {
    const monitor = this.monitors.get(callId);
    if (!monitor) {
      return;
    }

    clearInterval(monitor.intervalId);
    monitor.metricsSubject.complete();
    this.monitors.delete(callId);
  }

  public ngOnDestroy(): void {
    this.monitors.forEach((monitor) => clearInterval(monitor.intervalId));
    this.monitors.clear();
  }

  private getOrCreateMonitor(callId: CallId): CallMonitor {
    let monitor = this.monitors.get(callId);
    if (!monitor) {
      monitor = {
        metricsSubject: new Subject<CallQualityMetricsSnapshot>(),
        subscriberCount: 0,
        rollingTicks: [],
        history: [],
        jitterSumMs: 0,
        latencySumMs: 0,
        latencySampleCount: 0,
        packetsLostSum: 0,
        packetsTotalSum: 0,
        packetBaselineBySsrc: new Map(),
        held: false,
        heldIntervals: [],
      };
      this.monitors.set(callId, monitor);
    }

    return monitor;
  }

  private async sampleTick(callId: CallId, peerConnection: RTCPeerConnection): Promise<void> {
    const monitor = this.monitors.get(callId);
    // Also skip if a previous tick for this call is still awaiting getStats() -- without this, a
    // single slow getStats() call could let two ticks overlap and corrupt the packet-loss delta
    // state in extractRawMetrics, which assumes it's only ever read/written by one tick at a time.
    // Also skip entirely while on hold -- getStats() would only be reporting the MOH/silence path,
    // not real call media.
    if (!monitor || monitor.sampling || monitor.held) {
      return;
    }

    monitor.sampling = true;
    try {
      let stats: RTCStatsReport;
      try {
        stats = await peerConnection.getStats(null);
      } catch {
        return;
      }

      const raw = this.extractRawMetrics(monitor, stats);
      if (!raw) {
        return;
      }

      const now = Date.now();

      if (monitor.primarySsrc === undefined) {
        monitor.primarySsrc = raw.ssrc;
      }

      // Some hold implementations substitute an entirely different RTP source (an MOH file, played
      // server-side) for the call's real media, with no SDP/direction change to detect at all -- see
      // `CallMonitor.primarySsrc`. When that's happened, none of jitter/latency/packet-loss say
      // anything about the actual conversation's quality, so report NoData across the board rather
      // than measuring the MOH stream's own (unrelated) characteristics as if they were the call's.
      if (raw.ssrc !== undefined && raw.ssrc !== monitor.primarySsrc) {
        // The primary stream's packet counters sit frozen (not incrementing) while it isn't the
        // active SSRC, so its preserved baseline is about to go stale. Left alone, the tick where
        // the primary SSRC resumes would diff its (unchanged) counts against that stale baseline,
        // compute a deltaTotal of exactly 0, and -- per the *correct*, differently-motivated rule
        // that a stalled counter means total loss, not zero loss -- misreport a false 100% loss
        // spike. Clearing the baseline now means that resume tick instead correctly reports NoData
        // (no baseline yet), and a fresh, accurate baseline builds from that point on.
        if (monitor.primarySsrc !== undefined) {
          monitor.packetBaselineBySsrc.delete(monitor.primarySsrc);
        }
        // Mirrors endHold()'s reasoning: the live smoothing window shouldn't blend real pre-MOH
        // ticks with real post-MOH ticks across a substituted gap it doesn't know happened.
        monitor.rollingTicks = [];

        monitor.history.push({
          timestampMs: now,
          tier: CallQualityTier.NoData,
          jitterTier: CallQualityTier.NoData,
          latencyTier: CallQualityTier.NoData,
          packetLossTier: CallQualityTier.NoData,
        });
        monitor.metricsSubject.next({
          overallTier: CallQualityTier.NoData,
          jitter: { valueMs: 0, tier: CallQualityTier.NoData, jitterBufferMs: 0 },
          latency: { valueMs: 0, tier: CallQualityTier.NoData, rttMs: 0, relayed: false },
          packetLoss: {
            valuePct: 0,
            tier: CallQualityTier.NoData,
            packetsSent: 0,
            packetsReceived: 0,
            packetsLost: 0,
          },
        });
        return;
      }

      // Whole-call record: classify this raw tick directly (unsmoothed) so a brief blip and a
      // sustained problem are weighted by how long they actually lasted, not diluted by averaging.
      // Jitter always has a real reading from getStats(); latency/packet-loss report NoData instead
      // of classifying a 0-placeholder value as a real (and misleadingly good) Excellent reading.
      const rawJitterTier = this.classify(raw.jitterMs, CallQualityService.jitterThresholdsMs);
      const rawLatencyTier =
        raw.latencyMs !== undefined
          ? this.classify(raw.latencyMs, CallQualityService.latencyThresholdsMs)
          : CallQualityTier.NoData;
      const rawPacketLossTier =
        raw.packetLossPct !== undefined
          ? this.classify(raw.packetLossPct, CallQualityService.packetLossThresholdsPct)
          : CallQualityTier.NoData;
      const rawTier = this.combineWorst(rawJitterTier, rawLatencyTier, rawPacketLossTier);
      monitor.history.push({
        timestampMs: now,
        tier: rawTier,
        jitterTier: rawJitterTier,
        latencyTier: rawLatencyTier,
        packetLossTier: rawPacketLossTier,
      });
      monitor.jitterSumMs += raw.jitterMs;
      if (raw.latencyMs !== undefined) {
        monitor.latencySumMs += raw.latencyMs;
        monitor.latencySampleCount++;
      }
      monitor.packetsLostSum += raw.deltaLost;
      monitor.packetsTotalSum += raw.deltaTotal;

      // Live UI: average each metric across the rolling window before classifying, so a single
      // noisy tick can't flip the badge; the badge only reflects sustained conditions.
      monitor.rollingTicks.push({ timestampMs: now, ...raw });
      const cutoffMs = now - CallQualityService.rollingWindowMs;
      monitor.rollingTicks = monitor.rollingTicks.filter((tick) => tick.timestampMs >= cutoffMs);

      const isMeasured = (value: number | undefined): value is number => value !== undefined;
      const latencyValues = monitor.rollingTicks.map((tick) => tick.latencyMs).filter(isMeasured);
      const packetLossValues = monitor.rollingTicks.map((tick) => tick.packetLossPct).filter(isMeasured);

      const smoothedJitterMs = this.average(monitor.rollingTicks.map((tick) => tick.jitterMs));
      const smoothedLatencyMs = this.average(latencyValues);
      const smoothedPacketLossPct = this.average(packetLossValues);

      const jitterTier = this.classify(smoothedJitterMs, CallQualityService.jitterThresholdsMs);
      const latencyTier =
        latencyValues.length > 0
          ? this.classify(smoothedLatencyMs, CallQualityService.latencyThresholdsMs)
          : CallQualityTier.NoData;
      const packetLossTier =
        packetLossValues.length > 0
          ? this.classify(smoothedPacketLossPct, CallQualityService.packetLossThresholdsPct)
          : CallQualityTier.NoData;

      monitor.metricsSubject.next({
        overallTier: this.combineWorst(jitterTier, latencyTier, packetLossTier),
        jitter: { valueMs: smoothedJitterMs, tier: jitterTier, jitterBufferMs: raw.jitterBufferMs },
        latency: { valueMs: smoothedLatencyMs, tier: latencyTier, rttMs: raw.rttMs, relayed: raw.relayed },
        packetLoss: {
          valuePct: smoothedPacketLossPct,
          tier: packetLossTier,
          packetsSent: raw.packetsSent,
          packetsReceived: raw.packetsReceived,
          packetsLost: raw.packetsLost,
        },
      });
    } finally {
      monitor.sampling = false;
    }
  }

  /**
   * Extracts jitter, one-way latency, and packet loss for the current tick from a `getStats()`
   * report, plus supporting diagnostics for the detail panel's "Advanced Stats" (jitter buffer,
   * RTT, relayed-vs-direct, and cumulative packet counts). Packet loss uses cumulative counters,
   * so it's derived as a delta since the previous tick rather than the whole-call average.
   */
  private extractRawMetrics(
    monitor: CallMonitor,
    stats: RTCStatsReport
  ):
    | {
        jitterMs: number;
        /** Undefined when neither remote-inbound-rtp nor the candidate pair reported a round trip -- not a real 0ms measurement. `rttMs` below stays a diagnostic-only 0 placeholder for display purposes. */
        latencyMs: number | undefined;
        /** Undefined before there's a prior packet count for this SSRC to diff against -- not a real 0%/measurement. */
        packetLossPct: number | undefined;
        /** Packets lost/received+lost since the previous tick, for volume-weighted whole-call aggregation. */
        deltaLost: number;
        deltaTotal: number;
        jitterBufferMs: number;
        rttMs: number;
        relayed: boolean;
        packetsSent: number;
        packetsReceived: number;
        packetsLost: number;
        /** SSRC of the inbound-rtp stream this tick's stats came from -- see `CallMonitor.primarySsrc`. */
        ssrc: number | undefined;
      }
    | undefined {
    const byId = new Map<string, RtcStat>();
    let inbound: RtcStat | undefined;
    let outbound: RtcStat | undefined;
    let remoteInbound: RtcStat | undefined;
    let transport: RtcStat | undefined;
    const candidatePairs: RtcStat[] = [];

    stats.forEach((report: RtcStat) => {
      byId.set(report.id, report);
      switch (report.type) {
        case 'inbound-rtp': {
          // A stats snapshot can briefly contain more than one audio inbound-rtp report during an
          // SSRC transition (e.g. an MOH substitution) -- the old SSRC's entry can linger a tick or
          // two alongside the new one. Picking "whichever the iteration visits last" is arbitrary
          // and can flip inconsistently tick to tick; picking the one with the most recent
          // `lastPacketReceivedTimestamp` instead reliably identifies whichever stream is actually
          // delivering packets right now.
          if (
            report.kind === 'audio' &&
            (!inbound || (report.lastPacketReceivedTimestamp ?? 0) > (inbound.lastPacketReceivedTimestamp ?? 0))
          ) {
            inbound = report;
          }
          break;
        }
        case 'outbound-rtp': {
          if (report.kind === 'audio') {
            outbound = report;
          }
          break;
        }
        case 'remote-inbound-rtp': {
          remoteInbound = report;
          break;
        }
        case 'transport': {
          transport = report;
          break;
        }
        case 'candidate-pair': {
          candidatePairs.push(report);
          break;
        }
      }
    });

    if (!inbound) {
      return undefined;
    }

    const selectedPair =
      (transport?.selectedCandidatePairId ? byId.get(transport.selectedCandidatePairId) : undefined) ??
      candidatePairs.find((pair) => pair.nominated && pair.state === 'succeeded') ??
      candidatePairs.find((pair) => pair.state === 'succeeded');
    const localCandidate = selectedPair?.localCandidateId ? byId.get(selectedPair.localCandidateId) : undefined;
    const remoteCandidate = selectedPair?.remoteCandidateId ? byId.get(selectedPair.remoteCandidateId) : undefined;
    const relayed = localCandidate?.candidateType === 'relay' || remoteCandidate?.candidateType === 'relay';

    // Prefer the STUN/ICE-consent-based candidate-pair RTT over the RTCP-derived remote-inbound-rtp
    // one: ICE consent-freshness checks run independently of RTCP/media state, so they don't suffer
    // the same failure mode remote-inbound-rtp's SR/RR-based calculation does -- if RTCP exchange
    // paused during a hold, the first round-trip computed from a stale "last SR" reference after
    // resuming can be wildly, artifactually inflated (confirmed in testing: a fresh remote-inbound-rtp
    // reading of ~1.4s alongside a simultaneous, correct candidate-pair reading of ~0.12s). Falling
    // back to remote-inbound-rtp only when the candidate-pair reading isn't available.
    const rttSeconds = selectedPair?.currentRoundTripTime ?? remoteInbound?.roundTripTime;
    const hasRtt = rttSeconds != null;
    const rttMs = hasRtt ? rttSeconds * 1000 : 0;
    const latencyMs = hasRtt ? rttMs / 2 : undefined;
    const jitterMs = (inbound.jitter ?? 0) * 1000;

    const jitterBufferDelay = inbound.jitterBufferDelay ?? 0;
    const jitterBufferEmitted = inbound.jitterBufferEmittedCount ?? 0;
    const jitterBufferMs = jitterBufferEmitted > 0 ? (jitterBufferDelay / jitterBufferEmitted) * 1000 : 0;

    const packetsReceived = inbound.packetsReceived ?? 0;
    const packetsLost = inbound.packetsLost ?? 0;
    const baseline = inbound.ssrc !== undefined ? monitor.packetBaselineBySsrc.get(inbound.ssrc) : undefined;
    const hasPacketLossData = baseline !== undefined;
    const deltaReceived = hasPacketLossData ? packetsReceived - baseline.packetsReceived : 0;
    const deltaLost = hasPacketLossData ? packetsLost - baseline.packetsLost : 0;
    if (inbound.ssrc !== undefined) {
      monitor.packetBaselineBySsrc.set(inbound.ssrc, { packetsReceived, packetsLost });
    }

    const deltaTotal = deltaReceived + deltaLost;
    /**
     * `deltaTotal === 0` means two different things depending on whether a baseline exists yet.
     * On the very first tick there's nothing to diff against, so 0% is a neutral "unknown," not a
     * claim of zero loss. Once a baseline exists, a `deltaTotal` of 0 means inbound-rtp's counters
     * didn't move at all since the last tick -- not that nothing was lost, but that nothing arrived,
     * i.e. every packet expected this tick is gone. That's total loss (100%), not zero loss.
     */
    const packetLossPct = !hasPacketLossData ? undefined : deltaTotal > 0 ? (deltaLost / deltaTotal) * 100 : 100;

    return {
      jitterMs,
      latencyMs,
      packetLossPct,
      deltaLost,
      deltaTotal,
      jitterBufferMs,
      rttMs,
      relayed,
      packetsSent: outbound?.packetsSent ?? 0,
      packetsReceived,
      packetsLost,
      ssrc: inbound.ssrc,
    };
  }

  private classify(value: number, thresholds: { poor: number; bad: number }): CallQualityTier {
    if (value > thresholds.bad) {
      return CallQualityTier.Bad;
    }
    if (value > thresholds.poor) {
      return CallQualityTier.Poor;
    }

    return CallQualityTier.Excellent;
  }

  private combineWorst(...tiers: CallQualityTier[]): CallQualityTier {
    return tiers.reduce((worst, tier) =>
      CallQualityService.tierSeverity[tier] > CallQualityService.tierSeverity[worst] ? tier : worst
    );
  }

  private average(values: number[]): number {
    return values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
  }

  /**
   * Reduces a sequence of timestamped per-tick tiers into one overall tier by finding the single
   * worst `worstWindowMs`-long stretch of the call. Used both for the combined (worst-of-three)
   * tier and, independently, for each metric's own whole-call tier.
   *
   * Each sample's duration is the real elapsed time until the *next* recorded sample (last
   * observation carried forward), not a fixed `pollIntervalMs` slice -- ticks aren't guaranteed to
   * be exactly `pollIntervalMs` apart (a tick is skipped entirely whenever `extractRawMetrics` finds
   * no usable stats, and browsers throttle `setInterval` in backgrounded tabs/windows), so charging
   * every sample a flat duration would silently under- or over-count real elapsed time. The final
   * sample has no next tick to diff against, so it falls back to `pollIntervalMs`.
   *
   * Every second at a tier contributes `tierPoints` points; a sliding window (two pointers over the
   * already-chronological samples) tracks the points total within the trailing `worstWindowMs` and
   * `worstPoints` is the highest that total ever reaches. This naturally weighs severity and
   * duration together -- a short Bad blip and a longer Poor stretch can score the same -- without
   * the separate run-length/percentage/single-run-duration rules an earlier implementation needed
   * to approximate the same tradeoff.
   *
   * `heldIntervals` (wall-clock spans the call spent on hold) are subtracted out of each sample's
   * duration before it contributes any points. This is more than just "don't sample while held":
   * without it, the gap between the last pre-hold tick and the first post-hold tick would otherwise
   * have its whole span attributed to the pre-hold tier via the last-observation-carried-forward
   * logic above, silently smearing (e.g.) a Bad tier across an entire hold. Subtracting the overlap
   * makes held time contribute nothing at all, rather than inheriting a tier it never earned.
   *
   * A `NoData` sample's own duration is zeroed outright (no held-interval math needed): unlike a
   * hold, which leaves a gap with no samples at all, a `NoData` tick is still a real recorded sample
   * at its own timestamp -- there's no gap to smear across, just one slot that shouldn't count.
   */
  private rollupTiers(
    samples: Array<{ timestampMs: number; tier: CallQualityTier }>,
    heldIntervals: Array<{ startMs: number; endMs?: number }>
  ): {
    tier: CallQualityTier;
    worstPoints: number;
  } {
    if (samples.length === 0) {
      return { tier: CallQualityTier.Excellent, worstPoints: 0 };
    }

    const durationsMs = samples.map((sample, index) => {
      if (sample.tier === CallQualityTier.NoData) {
        return 0;
      }

      const nextMs =
        index + 1 < samples.length
          ? samples[index + 1].timestampMs
          : sample.timestampMs + CallQualityService.pollIntervalMs;
      const heldOverlapMs = heldIntervals.reduce((sum, hold) => {
        const overlapMs = Math.min(nextMs, hold.endMs ?? nextMs) - Math.max(sample.timestampMs, hold.startMs);
        return sum + Math.max(0, overlapMs);
      }, 0);

      return Math.max(0, nextMs - sample.timestampMs - heldOverlapMs);
    });

    const pointsAt = (index: number): number =>
      CallQualityService.tierPoints[samples[index].tier] * (durationsMs[index] / 1000);

    let windowPoints = 0;
    let windowMs = 0;
    let worstPoints = 0;
    let left = 0;
    for (let right = 0; right < samples.length; right++) {
      windowPoints += pointsAt(right);
      windowMs += durationsMs[right];
      while (windowMs > CallQualityService.worstWindowMs && left < right) {
        windowPoints -= pointsAt(left);
        windowMs -= durationsMs[left];
        left++;
      }
      worstPoints = Math.max(worstPoints, windowPoints);
    }

    let tier = CallQualityTier.Excellent;
    if (worstPoints > CallQualityService.badPointsLimit) {
      tier = CallQualityTier.Bad;
    } else if (worstPoints >= CallQualityService.poorPointsLimit) {
      tier = CallQualityTier.Poor;
    }

    return { tier, worstPoints: Math.round(worstPoints) };
  }

  private buildSummary(monitor: CallMonitor): CallQualitySummary {
    const overall = this.rollupTiers(
      monitor.history.map((tick) => ({ timestampMs: tick.timestampMs, tier: tick.tier })),
      monitor.heldIntervals
    );
    const jitter = this.rollupTiers(
      monitor.history.map((tick) => ({ timestampMs: tick.timestampMs, tier: tick.jitterTier })),
      monitor.heldIntervals
    );
    const latency = this.rollupTiers(
      monitor.history.map((tick) => ({ timestampMs: tick.timestampMs, tier: tick.latencyTier })),
      monitor.heldIntervals
    );
    const packetLoss = this.rollupTiers(
      monitor.history.map((tick) => ({ timestampMs: tick.timestampMs, tier: tick.packetLossTier })),
      monitor.heldIntervals
    );

    const sampleCount = monitor.history.length;

    return {
      tier: overall.tier,
      worstPoints: overall.worstPoints,
      jitterTier: jitter.tier,
      latencyTier: latency.tier,
      packetLossTier: packetLoss.tier,
      jitterAvgMs: monitor.jitterSumMs / sampleCount,
      latencyAvgMs: monitor.latencySampleCount > 0 ? monitor.latencySumMs / monitor.latencySampleCount : 0,
      packetLossAvgPct: monitor.packetsTotalSum > 0 ? (monitor.packetsLostSum / monitor.packetsTotalSum) * 100 : 0,
    };
  }
}
